import { Link } from "react-router-dom";
import { ShieldCheck, FileText, ScrollText, DoorOpen, Scale, Umbrella, ArrowRight } from "lucide-react";
import { FadeUp } from "@/components/Motion";
import CTASection from "@/components/CTASection";
import { IMAGES } from "@/lib/seed-content";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger
} from "@/components/ui/accordion";

const FAQS = [
  {
    q: "What exactly is the Year 9 exit option?",
    a: "After the 8th anniversary of the Supply Start Date, your trustees can give three months' written notice to stop purchasing electricity from SEED. From that point, SEED continues to own and maintain the system (which remains on your roof through year 25) and sells the electricity it produces to the grid. Your organisation simply walks away from any further purchase obligation. The option is written into the contract — not granted by goodwill.",
  },
  {
    q: "How is the kWh rate calculated over the 25 years?",
    a: "We describe SEED's rate as 'fixed' because it's set at signing and locked into your contract for the full 25-year term — never moving with volatile wholesale energy markets or at SEED's discretion. The single permitted adjustment is an annual escalator: in our standard agreement, UK CPI plus 1%, with the precise mechanism set out in your contract. There are no discretionary increases, and your trustees know the exact formula that produces next year's price before they sign.",
  },
  {
    q: "Who owns the equipment during the agreement?",
    a: "SEED owns the system for the full 25-year term of the agreement. Your building benefits from the electricity it generates, but you carry none of the asset risk and none of the maintenance burden. You are not required to insure the system itself — though, as is standard, you will be asked to maintain appropriate and adequate public liability cover at your premises.",
  },
  {
    q: "What happens if equipment fails or underperforms?",
    a: "Maintenance, monitoring and the operation of the system are written into the agreement for the full 25-year term as SEED's responsibility. The meters are remotely readable by both parties and SEED makes all generation data available to you. If equipment fails or requires unplanned maintenance, SEED arranges the remedy at its own expense.",
  },
  {
    q: "What happens if our building is sold or our organisation restructures?",
    a: "The agreement contains standard assignment provisions: any transfer of the Client's obligations requires SEED's prior written consent, which we work with your solicitor to facilitate. Force majeure and termination provisions are also written into the agreement and we recommend reviewing them with legal counsel before signing.",
  },
  {
    q: "How is this different from buying solar with our own capital?",
    a: "Outright purchase requires significant capital, ongoing maintenance responsibility, and exposure to performance risk. Our model removes all three. You pay only for the electricity generated, at a fixed kWh rate set at signing — and the maintenance burden never reaches the trustees' agenda.",
  },
];

export default function TrusteeHub() {
  return (
    <div data-testid="trustee-hub-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-white" data-testid="trustee-hero">
        <div className="container-seed pt-16 pb-12 lg:pt-24 lg:pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <FadeUp>
                <div className="text-overline">The Trustee Hub</div>
              </FadeUp>
              <FadeUp delay={1}>
                <h1 className="mt-4 font-display font-extrabold text-slate-900 tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
                  Plain-English answers<br />for governance-grade decisions.
                </h1>
              </FadeUp>
              <FadeUp delay={2}>
                <p className="mt-6 text-slate-600 text-base sm:text-lg max-w-xl leading-relaxed">
                  Trustees carry personal responsibility for decisions made on behalf of an organisation. This
                  hub is designed to give you the exact information you need — without the marketing gloss — to
                  bring a recommendation to your board.
                </p>
              </FadeUp>
            </div>
            <FadeUp delay={2} className="lg:col-span-5">
              <div className="rounded-2xl overflow-hidden border border-slate-200 bg-slate-50">
                <img
                  src={IMAGES.trusteeShield}
                  alt="Legal security and protection visualisation"
                  className="w-full h-[360px] object-contain p-6"
                />
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* THREE PILLARS */}
      <section className="container-seed pb-8" data-testid="trustee-pillars">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { icon: DoorOpen, title: "Year 9 Exit Option", body: "After the 8th anniversary of the Supply Start Date, give three months' notice to stop purchasing. The system stays on your roof to year 25 and SEED sells the output to the grid. Written into the agreement, not granted by goodwill.", testId: "pillar-year9" },
            { icon: Scale, title: "How the Rate Works", body: "We describe the rate as 'fixed' because it's set at signing and locked into your contract for 25 years — never moving with volatile wholesale markets or at SEED's discretion. The single permitted adjustment is an annual escalator of UK CPI plus 1%, published in the contract before you sign.", testId: "pillar-rate" },
            { icon: Umbrella, title: "Maintenance & Performance", body: "Full maintenance, monitoring and performance commitments are written into the agreement for the duration of the term — there are no surprise invoices, and trustees never carry the operating burden.", testId: "pillar-maintenance" },
          ].map((p, i) => (
            <FadeUp key={p.title} delay={i}>
              <div data-testid={p.testId} className="bg-white border border-slate-200 rounded-xl p-8 h-full">
                <div className="w-12 h-12 rounded-lg bg-slate-900 flex items-center justify-center">
                  <p.icon className="w-6 h-6 text-emerald-400" strokeWidth={2} />
                </div>
                <h3 className="mt-6 font-display font-bold text-slate-900 text-xl">{p.title}</h3>
                <p className="mt-3 text-slate-600 leading-relaxed text-sm">{p.body}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* IN DEPTH */}
      <section className="container-seed section-pad" data-testid="trustee-in-depth">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <div className="text-overline">In depth</div>
            <h2 className="mt-3 font-display font-bold text-slate-900 text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
              The questions every responsible board will ask.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Below are the six questions that come up at every trustee meeting. We've answered each in the
              language your governance documents already use.
            </p>
            <a
              href="/SEED-Legals-Key-Point-Summary.pdf"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="trustee-download-pack-btn"
              className="mt-8 inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-semibold py-3 px-5 rounded-lg group"
            >
              <FileText className="w-4 h-4" />
              Download — Legals: Key Point Summary (PDF)
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </a>
            <p className="mt-3 text-xs text-slate-500 leading-relaxed">
              A plain-summary guide drawn from SEED's standard 25-year agreement — no email required.
            </p>
          </div>

          <div className="lg:col-span-7">
            <Accordion type="single" collapsible className="w-full" data-testid="trustee-faq">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`} className="border border-slate-200 rounded-xl mb-3 bg-white px-5">
                  <AccordionTrigger
                    data-testid={`faq-trigger-${i}`}
                    className="text-left font-display font-semibold text-slate-900 text-base sm:text-lg hover:no-underline py-5"
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 leading-relaxed text-sm pb-5">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* DUTY OF CARE STRIP */}
      <section className="bg-slate-900 text-white" data-testid="trustee-duty">
        <div className="container-seed py-16 lg:py-20 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <FadeUp>
            <div className="flex items-center gap-3">
              <ShieldCheck className="w-6 h-6 text-emerald-400" />
              <div className="text-overline text-emerald-400">Duty of care, in writing</div>
            </div>
            <h3 className="mt-4 font-display font-bold text-white text-2xl sm:text-3xl tracking-tight leading-[1.1]">
              We exist to make this decision easy to defend at any future trustees' meeting.
            </h3>
          </FadeUp>
          <FadeUp delay={1}>
            <p className="text-slate-300 leading-relaxed">
              Every clause, schedule and warranty is drafted to be defensible: by the trustees who sign it
              today, and by the trustees who inherit it ten or twenty years from now. If you can't explain a
              clause to a new lay trustee in two minutes, we've drafted it wrong.
            </p>
            <ScrollText className="w-6 h-6 text-emerald-400 mt-6" />
          </FadeUp>
        </div>
      </section>

      <CTASection
        overline="Tailored proposal"
        title="Ready for a tailored proposal for your building?"
        subtitle="Send us a recent bill, the building address and any constraints. We'll come back with a site-specific opening kWh rate and a draft agreement your solicitor can review."
        ctaLabel="Book Assessment"
        testId="trustee-cta-section"
      />
    </div>
  );
}
